import AnswerItem from './answer_item'
import SkillItem from './skill_item'
import Skill from './skill'
import { filter } from 'lodash'
import UserExam from '../../../client/models/user_exam'
import Assessment from '../../../client/models/assessment'
import { AnswerFlowQuestion } from './exam'
import MarkdownBodyParser from '../../../../lib/MarkdownBodyParser'
import { QuestionFieldType } from '../../../../common/QuestionFieldType'
import Lesson from './lesson'
import Course from './course'
import Answer from '../../../client/models/answer'

export default class Question extends QuestionFieldType {
  readonly id: number
  readonly body: string
  readonly explanation: string
  readonly examId: string
  readonly createdAt: string
  readonly updatedAt: string
  readonly image: string
  readonly publishedAt: string
  readonly test: boolean
  readonly htmlBody: string
  readonly group: number
  readonly maxScore: number
  readonly userScore: number
  readonly correctRate: string
  readonly headerTitle: string
  readonly answerItems: AnswerItem[]
  readonly skillItems: SkillItem[]
  readonly skills: Skill[]
  readonly lessons: Lesson[]
  readonly kind: ExamTestQuestionKind
  answers: Answer[] = []

  constructor(data: { [key: string]: any }) {
    super(data.fieldType)
    this.id = data.id
    this.body = data.body
    this.explanation = data.explanation
    this.examId = data.examId
    this.createdAt = data.createdAt
    this.updatedAt = data.updatedAt
    this.image = data.image
    this.publishedAt = data.publishedAt
    this.test = data.test
    this.htmlBody = data.htmlBody
    this.group = data.group
    this.maxScore = data.maxScore
    this.headerTitle = data.headerTitle
    this.kind = data.kind
    this.userScore = data.userScore

    if (data.answerItems) {
      this.answerItems = data.answerItems.map((element) => new AnswerItem(element))
    }
    if (data.skillItems) {
      this.skillItems = data.skillItems.map((element) => new SkillItem(element))
    }
    if (data.skills) {
      this.skills = data.skills.map((element) => new Skill(element))
    }
    if (data.lessons) {
      this.lessons = data.lessons.map((element) => new Lesson(element))
    }
    if (data.as) {
      this.answers = data.as.map(element => new Answer(element))
    }
  }

  get coursesWithLessons(): {
    course: Course,
    lessons: Lesson[]
  }[]
  {
    const results: {
      course: Course,
      lessons: Lesson[]
    }[] = []

    this.lessons.forEach(lesson => {

      lesson.courses.forEach(course => {
        const result = results.find(item => item.course.id == course.id )
        if (result) {
          result.lessons.push(lesson)
        }else{
          results.push({
            course,
            lessons: [lesson]
          })
        }
      });
    });

    return results
  }

  get answerItemsView() {
    return filter(this.answerItems, o => o.name != 'point')
  }

  get isTestQuestion(): boolean {
    return this.kind == ExamTestQuestionKind.QUESTION
  }

  get isWorkSheet(): boolean {
    return this.kind == ExamTestQuestionKind.WORK_SHEET
  }

  get isDescriptionQuestion(): boolean {
    return this.kind == ExamTestQuestionKind.DESCRIPTION
  }

  getMaxScore(){
    // 採点質問じゃなければ、点数は質問の点数です。
    if (!this.isTextarea) {
      return this.maxScore
    }

    // 採点の場合、スキルアイテムごとに点数設定出来ますので、それを計算しないといけません。
    return this.skillItems.reduce((sum, skillItem) => {
      let skillItemScore = this.maxScore
      if (skillItem.answerItems.length) {
        // Get max of answerItems
        skillItemScore = skillItem.answerItems.reduce((max, answerItem) => max > answerItem.allotment ? max : answerItem.allotment, 0)
      }
      return sum + skillItemScore
    }, 0)
  }

  getAssessmentsByUserExam(userExam: UserExam): Assessment[] {
    if (!userExam) {
      return []
    }
    const assessments = userExam.getAssessmentByQuestion(this.id)
    return assessments
  }

  getScoreByUserExam(userExam: UserExam): number {
    const assessments = this.getAssessmentsByUserExam(userExam)
    // 採点質問じゃなければ、点数は一件のassessmentを使います
    if (!this.isTextarea) {
      return assessments.length ? assessments[0].score : 0
    }

    // 採点の場合、点数は合計しないといけません
    return assessments.reduce((sum, assessment) => sum + assessment.score, 0)
  }

  getAnswerValue(answerFlowQuestions: AnswerFlowQuestion[]) {
    if (!answerFlowQuestions || !Array.isArray(answerFlowQuestions)) {
      return []
    }
    const indexAnswer = answerFlowQuestions.findIndex(item => item && item.questionId === this.id)
    return indexAnswer !== -1 ? answerFlowQuestions[indexAnswer].value : []
  }
  
  get answerValues(){
    if (this.isTextfield && this.answerItems.length != this.answers.length) {
      return this.answerItems.map(answerItem => {
        const answer = this.answers.find(answer => answer.answerItemId == answerItem.id)
        return answer ? answer.body : ''
      })
    }

    return this.answers.map(answer => answer.body)
  }

  // textareaしか使われていません
  get textareaRowNumber(): number {
    if (!this.isTextarea) {
      return 0
    }
    var parser = new MarkdownBodyParser(this.body)
    return parser.textareaRowNumber
  }
}

export class GroupQuestion {
  constructor(public readonly groupId: number, public questions: Question[]) {}

  getQuestionPoint(userExam: UserExam) {
    const totalScore = this.getScoreByUserExam(userExam)
    return this.maxScore ? Math.round((totalScore * 100) / this.maxScore) : 0
  }

  getAssessmentsByUserExam(userExam: UserExam): Assessment[] {
    if (!userExam) {
      return []
    }
    let assessments = []
    this.questions.forEach((question) => {
      assessments = [...assessments, ...userExam.getAssessmentByQuestion(question.id)]
    })
    return assessments
  }

  getScoreByUserExam(userExam: UserExam): number {
    let score = 0
    if (!userExam) {
      return 0
    }
    this.questions.forEach((question) => {
      score += question.getScoreByUserExam(userExam)
    })
    return score
  }

  getCorrectCountByUserExam(userExam: UserExam): number {
    if (!userExam) {
      return 0
    }
    let correctCount = 0
    this.questions.forEach((question) => {
      const assessments = userExam.getAssessmentByQuestion(question.id)

      let score = 0
      assessments.forEach((assessment) => {
        score += assessment.score
      })

      if (score === question.maxScore) {
        correctCount += 1
      }
    })
    return correctCount
  }

  get userScore(): number {
    let userScore = 0
    this.questions.forEach((question) => {
      userScore += question.userScore
    })
    return userScore
  }

  get maxScore(): number {
    let totalMaxScore = 0
    this.questions.forEach((question) => {
      totalMaxScore += question.getMaxScore()
    })
    return totalMaxScore
  }

  get isDescriptionQuestion(): boolean {
    return this.questions.every((item) => item.isDescriptionQuestion)
  }

  get isWorkSheet(): boolean {
    return this.questions.every((item) => item.isWorkSheet)
  }

  get isTest(): boolean {
    return this.questions.some((item) => item.isTestQuestion)
  }

  get skills(): Skill[] {
    const skills: Skill[] = []
    this.questions.forEach((question) => {
      question.skills.forEach((skill) => {
        if (!skills.find((item) => item.id === skill.id)) {
          skills.push(skill)
        }
      })
    })

    return skills
  }

  get skillItems(): SkillItem[] {
    const skillItems: SkillItem[] = []
    this.questions.forEach((question) => {
      question.skillItems.forEach((skillItem) => {
        if (!skillItems.find((item) => item.id === skillItem.id)) {
          skillItems.push(skillItem)
        }
      })
    })

    return skillItems
  }
}

enum ExamTestQuestionKind{
  QUESTION = "question",
  DESCRIPTION = "description",
  WORK_SHEET = "work_sheet",
}
export class ExamTestQuestion {
  readonly id: number
  readonly group: number
  readonly headerTitle: string
  readonly score: number
  readonly userScore: number
  readonly maxScore: number
  readonly hasAnswer: boolean
  readonly kind: ExamTestQuestionKind
  readonly test: boolean

  constructor(data: { [key: string]: any }){
    this.id = data.id
    this.group = data.group
    this.headerTitle = data.headerTitle
    this.score = data.score
    this.userScore = data.userScore
    this.maxScore = data.maxScore
    this.hasAnswer = data.hasAnswer
    this.kind = data.kind
    this.test = data.test
  }

  get isAnswered(){
    return this.hasAnswer
  }

  get isTestQuestion(): boolean {
    return this.kind == ExamTestQuestionKind.QUESTION
  }

  get isWorkSheet(): boolean {
    return this.kind == ExamTestQuestionKind.WORK_SHEET
  }

  get isDescriptionQuestion(): boolean {
    return this.kind == ExamTestQuestionKind.DESCRIPTION
  }
}
export class GroupExamTestQuestion {
  constructor(public readonly groupId: number, public questions: ExamTestQuestion[]) {}

  get maxScore(){
    return this.questions.reduce((a, b) => a + b.maxScore, 0)
  }
  get score(){
    return this.questions.reduce((a, b) => a + b.score, 0)
  }

  get isAnswered(){
    return this.questionCount == this.questionAnsweredCount
  }

  get questionCount() {
    return this.questions.length
  }

  get questionAnsweredCount() {
    return this.questions.filter((item) => item.isAnswered).length
  }
}

export class ExamResultQuestion extends ExamTestQuestion{
  readonly answerItems: AnswerItem[] = []
  constructor(data: { [key: string]: any }){
    super(data)
    if (data.answerItems) {
      this.answerItems = data.answerItems.map((element) => new AnswerItem(element))
    }
  }
}
export class GroupExamResultQuestion extends GroupExamTestQuestion {
  constructor(public readonly groupId: number, public questions: ExamResultQuestion[]) {
    super(groupId, questions)
  }
}
